import React from 'react';
import PropTypes from 'prop-types';
import {AccountNumberMask, Button, Card, Column, DescriptiveText, Row, TextInput} from '../../../shared';
import IconDropdown from '../../../shared/components/IconDropdown';
import {formatter} from '../../../shared/utils/utils';
import history from '../../../history';
import {connect} from 'react-redux';
import Popover from '../../../shared/components/Popover';

const AccountCard = ({institution, account, removeAccount, editAccount, session}) => {
  const actionOptions = [
    {
      label: 'Remove Account',
      isDisabled: account.sourceType !== 'MANUAL' || !!session.runAsInvestor,
      disabledHelpText: account.sourceType === 'AGGREGATOR'
        ? 'You are unable to remove aggregated accounts, instead you can remove the entire institution.'
        : 'You are unable to remove accounts provided by your advisor and the firm they represent.',
      action: () => removeAccount(account.id),
    },
    {
      label: 'Edit Account',
      action: () => editAccount(account),
      isDisabled: !!session.runAsInvestor,
    },
  ];

  const accountName = account.nickname || account.name;

  return <Column key={account.id} alignItems={'left'} width={'100%'} height={'fit-content'} margin={'5px'}>
    <Card width={'100%'} margin={'0'}>
      <Row justifySubRow={'right'} wrap={false}>
        <IconDropdown icon={'threeDot'}
                      options={{mainSection: {options: actionOptions}}}
                      iconWidth={'30px'}
                      iconHeight={'30px'}/>
      </Row>
      <Row justifySubRow={'space between'} wrap={false}>
        <Column alignItems={'left'}>
          <DescriptiveText alignment={'left'}
                           width={'fit-content'}
                           fontSize={'18px'}
                           font={'ProximaSemibold'}
                           opacity={0.95}
                           margin={'5px 5px 2px 5px'}>
            {formatter('currency')(account.balance)}
          </DescriptiveText>
          <DescriptiveText alignment={'left'} width={'fit-content'} fontSize={'12px'} font={'ProximaLight'} opacity={0.85}>
            As of {formatter('date')(account.asOfDate)}
          </DescriptiveText>
        </Column>
        <Popover trigger={'hover'}
                 placement={'top'}
                 content={`The dashboard view is not available for ${account.typeName} account type`}
                 isControlled={account.highLevel === 'ASSETS'}>
          <Button importance={'primary'}
                  size={'md'}
                  label={'View'}
                  width={'100px'}
                  disabled={account.highLevel !== 'ASSETS'}
                  action={() => history.push(`/accounts/${account.containerID}`)}/>
        </Popover>
      </Row>
      <Column alignItems={'left'} width={'100%'} wrap={false} margin={'5px 0 2px 0'}>
        <DescriptiveText alignment={'left'}
                         fontSize={'20px'}
                         maxWidth={'fill'}
                         font={'ProximaSemibold'}
                         opacity={0.95}
                         ellipsisOverflow={{
                           popoverContent: accountName,
                           popoverPlacement: 'top-start',
                         }}>
          {accountName}
        </DescriptiveText>
        <DescriptiveText alignment={'left'} width={'fit-content'} fontSize={'14px'} opacity={0.95}>
          <AccountNumberMask number={account.number} institution={institution} institutionName={institution.name}/>
        </DescriptiveText>
      </Column>
      <TextInput type={'text'}
                 title={'Type'}
                 placeholder={'Account Type'}
                 dataPoint={{name: 'account', value: account.typeName}}
                 readOnly/>
    </Card>
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

export default connect(mapStateToProps, null)(AccountCard);

AccountCard.propTypes = {
  /**
   * Session
   */
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * The institution object
   */
  institution: PropTypes.object.isRequired,
  /**
   * The institution's account object
   */
  account: PropTypes.object.isRequired,
  /**
   * The function that opens "Remove Account" modal
   */
  removeAccount: PropTypes.func.isRequired,
  /**
   * The function that opens "Edit Account" modal
   */
  editAccount: PropTypes.func.isRequired,
};
